import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { SchemaProperty } from 'types/schema';
import type { RenderMode, RendererRef } from 'types/aspect';
import UnifiedPropertyRendererMolecule from '../UnifiedPropertyRenderer/UnifiedPropertyRendererMolecule';
import AdditionalPropertiesGrid from './AdditionalPropertiesGrid';
import { fanOutAspectBagChange } from '../../../utils/fanOutAspectBagChange';

export interface WholeAspectRendererProps {
  aspectSystemName: string;
  ref_: RendererRef;
  properties: SchemaProperty[];
  values: Record<string, any>;
  mode: RenderMode;
  onChange: (propertyName: string, value: any) => void;
  writeBlockedProps?: Set<string>;
  editable?: boolean;
  onDockedPropertyEdit?: (propertyName: string) => void;
}

/**
 * Mounts one aspect's declared whole-aspect component in place of its contributed properties'
 * flattened rows (EAINT-855 SC1). Synthesizes a minimal `SchemaProperty` carrying the aspect's
 * value bag (keyed by contributed property name) since `UnifiedPropertyRendererMolecule`'s contract is
 * per-property, not per-aspect — `resolvedOverride` skips its internal `resolveRenderer` call so
 * this ref (already resolved above, against the aspect-level chain) is used verbatim.
 *
 * `groupPropertiesByAspect` can only mountability-check a `catalog` ref synchronously (the adapter
 * registry lookup is pure); a `composition` ref's mountability is only knowable after its async
 * fetch settles. This component is therefore the SECOND, reactive half of the "never blank" edge
 * case (H-3, EAINT-855 review pass 2): if the composition genuinely fails to resolve (unpublished,
 * deleted, 404), it falls back to rendering this group's properties as ordinary flattened rows via
 * `AdditionalPropertiesGrid` — the same fallback the pure grouping step already applies for an
 * unmountable catalog ref — instead of leaving `CompositionRendererBridge`'s generic
 * unknown-component placeholder standing in for a whole aspect.
 */
const WholeAspectRenderer: React.FC<WholeAspectRendererProps> = ({
  aspectSystemName,
  ref_,
  properties,
  values,
  mode,
  onChange,
  writeBlockedProps,
  editable,
  onDockedPropertyEdit,
}) => {
  const aspectValueBag = useMemo(() => {
    const bag: Record<string, any> = {};
    for (const prop of properties) bag[prop.name] = values[prop.name];
    return bag;
  }, [properties, values]);

  const pseudoProperty = useMemo(
    () =>
      ({
        name: aspectSystemName,
        type: 'json',
        rules: [],
        hidden: false,
        required: false,
        read_only: false,
        is_inherited: false,
        inherited_from_schema: null,
        default_value: null,
      }) as unknown as SchemaProperty,
    [aspectSystemName]
  );

  const writeBlockedInGroup = properties.filter((p) => writeBlockedProps?.has(p.name)).map((p) => p.name);

  // Reset whenever the resolved ref itself changes (different aspect, different mode, a fresh
  // composition version) — a stale failure must not stick past the ref that caused it. Gated on
  // an ACTUAL change (via the ref-held previous key), not on mount: `CompositionRendererBridge`'s
  // own resolution-failed effect is in a DESCENDANT, so it commits before this effect (children
  // run first) — an unconditional reset here would fire right after on the very same mount and
  // clobber the `true` it just set, permanently hiding the fallback.
  const [compositionMountFailed, setCompositionMountFailed] = useState(false);
  const refKey = `${ref_.origin}:${ref_.name}:${ref_.version ?? ''}`;
  const previousRefKey = useRef(refKey);
  useEffect(() => {
    if (previousRefKey.current === refKey) return;
    previousRefKey.current = refKey;
    setCompositionMountFailed(false);
  }, [refKey]);

  const content = ref_.origin === 'composition' && compositionMountFailed ? (
    <AdditionalPropertiesGrid
      properties={properties}
      editable={editable}
      onDockedPropertyEdit={onDockedPropertyEdit}
      writeBlockedProps={writeBlockedProps}
    />
  ) : (
    <UnifiedPropertyRendererMolecule
      property={pseudoProperty}
      mode={mode}
      value={aspectValueBag}
      onChange={
        mode === 'edit'
          ? (newBag: unknown) => fanOutAspectBagChange(newBag, properties, aspectValueBag, onChange, writeBlockedProps)
          : undefined
      }
      resolvedOverride={{ ref: ref_, source: 'explicit' }}
      extraProps={writeBlockedInGroup.length > 0 ? { writeBlockedProperties: writeBlockedInGroup } : undefined}
      onCompositionResolutionFailed={ref_.origin === 'composition' ? () => setCompositionMountFailed(true) : undefined}
    />
  );

  return <div data-aspect-system-name={aspectSystemName}>{content}</div>;
};

export default WholeAspectRenderer;
